<template>
  <div class="empty-shell">
    <!-- Hero -->
    <section class="hero">
      <div class="hero__content">
        <span class="pill pill--soft">No se encontraron pacientes</span>
        <h1>Construye tu primer expediente digital</h1>
        <p>
          Centraliza datos, notas, recetas, estudios y alertas en un solo lugar. Crea tu primer paciente y activa todo el flujo clínico.
        </p>
        <div class="hero__actions">
          <button class="btn btn--primary" @click="$emit('createPatient')">
            <i class="fas fa-user-plus"></i>
            Crear paciente
          </button>
          <button class="btn btn--ghost" @click="activeFaq = faqs[0].id">
            <i class="fas fa-compass"></i>
            Ver cómo empezar
          </button>
        </div>
        <div class="hero__badges">
          <div
            v-for="item in heroHighlights"
            :key="item.id"
            class="hero-card"
          >
            <div class="hero-card__icon" :class="item.theme">
              <i :class="item.icon"></i>
            </div>
            <p class="hero-card__title">{{ item.title }}</p>
            <small>{{ item.description }}</small>
          </div>
        </div>
      </div>
      <div class="hero__glass">
        <div class="glass__stack">
          <div class="glass__tile">
            <i class="fas fa-heartbeat"></i>
            <div>
              <p class="kpi-label">Secciones clave</p>
              <p class="kpi-value">Historia clínica · Notas · Recetas</p>
            </div>
          </div>
          <div class="glass__tile">
            <i class="fas fa-cloud-upload-alt"></i>
            <div>
              <p class="kpi-label">Documentos</p>
              <p class="kpi-value">Laboratorios, imágenes y anexos</p>
            </div>
          </div>
          <div class="glass__tile">
            <i class="fas fa-bolt"></i>
            <div>
              <p class="kpi-label">Automatiza</p>
              <p class="kpi-value">Plantillas, firma digital y PDFs</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Resumen del expediente -->
    <section class="modules">
      <div class="section-head">
        <div>
          <p class="eyebrow">Todo lo que guarda el expediente</p>
          <h2>Mapa rápido de información del paciente</h2>
          <p class="muted">
            Visualiza qué puedes registrar y cómo se conecta: identidad, clínica, tratamientos y archivos de respaldo.
          </p>
        </div>
        <button class="chip" @click="$emit('createPatient')">
          <i class="fas fa-plus-circle"></i>
          Crear paciente
        </button>
      </div>
      <div class="modules__grid">
        <div v-for="module in modules" :key="module.id" class="module-card">
          <div class="module-card__header">
            <span class="module-icon" :style="{ background: module.color }">
              <i :class="module.icon"></i>
            </span>
            <div>
              <p class="module-label">{{ module.purpose }}</p>
              <h3>{{ module.title }}</h3>
            </div>
          </div>
          <p class="module-desc">{{ module.description }}</p>
          <div class="module-tags">
            <span v-for="(item, idx) in module.items" :key="idx">{{ item }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Plantillas de uso frecuente -->
    <section class="templates-hint">
      <div class="templates-card">
        <div class="templates-icon">
          <i class="fas fa-layer-group"></i>
        </div>
        <div>
          <p class="eyebrow">Plantillas de uso frecuente</p>
          <h3>Recetas listas en segundos</h3>
          <p class="muted">
            Guarda tus combinaciones habituales de medicamentos e indicaciones para reutilizarlas en un clic. Personaliza dosis y duración antes de generar el PDF con firma digital.
          </p>
        </div>
      </div>
    </section>

    <!-- Catálogo de medicamentos -->
    <section class="templates-hint">
      <div class="templates-card">
        <div class="templates-icon alt">
          <i class="fas fa-pills"></i>
        </div>
        <div>
          <p class="eyebrow">Catálogo de medicamentos</p>
          <h3>Busca, filtra y prescribe con precisión</h3>
          <p class="muted">
            Navega por el catálogo, consulta presentaciones y dosis, guarda combinaciones favoritas y aplica indicaciones claras en segundos antes de generar la receta en PDF.
          </p>
        </div>
      </div>
    </section>

    <!-- Primeros pasos -->
    <section class="steps">
      <div class="section-head center">
        <div>
          <p class="eyebrow">Primeros pasos</p>
          <h2>Guía rápida antes de crear tu primer paciente</h2>
          <p class="muted">
            Sigue este flujo simple: registra identidad, agrega antecedentes clave y genera la primera nota o receta.
          </p>
        </div>
      </div>
      <div class="steps__grid">
        <div v-for="(step, index) in steps" :key="step.id" class="step-card">
          <div class="step-number">{{ index + 1 }}</div>
          <div class="step-content">
            <h3>{{ step.title }}</h3>
            <p>{{ step.description }}</p>
            <ul v-if="step.tips" class="step-tips">
              <li v-for="(tip, idx) in step.tips" :key="idx">
                <i class="fas fa-sparkles"></i>
                <span>{{ tip }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQs -->
    <section class="faq">
      <div class="section-head center">
        <div>
          <p class="eyebrow">Ayuda rápida</p>
          <h2>Preguntas frecuentes</h2>
          <p class="muted">Las dudas más comunes al comenzar. Toca para desplegar.</p>
        </div>
      </div>
      <div class="faq__list">
        <div
          v-for="faq in faqs"
          :key="faq.id"
          class="faq__item"
          :class="{ 'is-open': activeFaq === faq.id }"
          @click="toggleFaq(faq.id)"
        >
          <div class="faq__question">
            <h3>{{ faq.question }}</h3>
            <i class="fas" :class="activeFaq === faq.id ? 'fa-minus' : 'fa-plus'"></i>
          </div>
          <transition name="faq-answer">
            <div v-if="activeFaq === faq.id" class="faq__answer">
              <p>{{ faq.answer }}</p>
            </div>
          </transition>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="cta">
      <div class="cta__content">
        <div>
          <p class="eyebrow light">Siguiente paso</p>
          <h2>Registra tu primer paciente y activa el expediente</h2>
          <p class="muted light">
            Un clic para crear y empezar a documentar consultas, recetas y estudios con tu branding.
          </p>
        </div>
        <button class="btn btn--primary solid" @click="$emit('createPatient')">
          <i class="fas fa-rocket"></i>
          Crear paciente ahora
        </button>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref } from 'vue';

defineEmits(['createPatient']);

const activeFaq = ref(null);

const heroHighlights = [
  {
    id: 1,
    title: 'Expediente vivo',
    description: 'Identidad, alertas, alergias y contactos de emergencia visibles en todo momento.',
    icon: 'fas fa-id-card-alt',
    theme: 'theme-blue'
  },
  {
    id: 2,
    title: 'Notas y evolución',
    description: 'Consulta, exploración, diagnósticos CIE-10 y plan en un flujo ágil.',
    icon: 'fas fa-notes-medical',
    theme: 'theme-indigo'
  },
  {
    id: 3,
    title: 'Recetas listas',
    description: 'Plantillas, firma digital y generación inmediata en PDF con tu membrete.',
    icon: 'fas fa-prescription-bottle-alt',
    theme: 'theme-green'
  }
];

const modules = [
  {
    id: 1,
    title: 'Perfil y alertas',
    purpose: 'Identidad y seguridad',
    icon: 'fas fa-user-shield',
    color: 'rgba(99, 102, 241, 0.18)',
    description: 'Datos personales, foto, contactos, alertas críticas y alergias visibles en todo el expediente.',
    items: ['Datos básicos', 'Alertas rápidas', 'Alergias', 'Contacto de emergencia']
  },
  {
    id: 2,
    title: 'Historia clínica',
    purpose: 'Antecedentes',
    icon: 'fas fa-heartbeat',
    color: 'rgba(16, 185, 129, 0.18)',
    description: 'Antecedentes con autoguardado: personales y familiares, patológicos y no patológicos, gineco-obstétricos, control de embarazo, plan/tratamiento y signos vitales.',
    items: [
      'Antecedentes personales y familiares',
      'Patológicos y no patológicos',
      'Gineco-obstétricos',
      'Control de embarazo',
      'Plan y tratamiento',
      'Signos vitales'
    ]
  },
  {
    id: 3,
    title: 'Notas médicas',
    purpose: 'Evolución',
    icon: 'fas fa-file-medical',
    color: 'rgba(59, 130, 246, 0.18)',
    description: 'Consulta, exploración física, diagnósticos CIE-10 y planes de seguimiento en línea de tiempo.',
    items: ['Motivo y exploración', 'Diagnósticos CIE-10', 'Plan terapéutico', 'Seguimiento']
  },
  {
    id: 4,
    title: 'Recetas y tratamientos',
    purpose: 'Prescripción',
    icon: 'fas fa-prescription',
    color: 'rgba(14, 165, 233, 0.18)',
    description: 'Catálogo de medicamentos, indicaciones claras, firma digital y exportación a PDF. Usa plantillas de recetas frecuentes para agilizar tus prescripciones.',
    items: [
      'Catálogo de medicamentos',
      'Indicaciones y dosis',
      'Plantillas de uso frecuente',
      'Firma digital',
      'PDF inmediato'
    ]
  },
  {
    id: 5,
    title: 'Documentos y estudios',
    purpose: 'Soporte',
    icon: 'fas fa-folder-open',
    color: 'rgba(245, 158, 11, 0.18)',
    description: 'Sube laboratorios, imágenes y estudios externos, con vista previa firmada, clasificación por carpetas y respaldo en Google Cloud para seguridad y disponibilidad.',
    items: [
      'Laboratorios y anexos',
      'Imágenes con vista previa',
      'Clasificación por carpetas',
      'Firma digital en PDF',
      'Almacenado en Google Cloud'
    ]
  },
  {
    id: 6,
    title: 'Datos personalizados',
    purpose: 'Grids y tablas',
    icon: 'fas fa-table',
    color: 'rgba(236, 72, 153, 0.18)',
    description: 'Tablas con filas y columnas renombrables, autoguardado, plantillas reutilizables por paciente y exportación de datos estructurados a CSV.',
    items: [
      'Columnas editables y renombrables',
      'Filas y datos estructurados',
      'Autoguardado en grids',
      'Plantillas por paciente',
      'Exportación a CSV'
    ]
  }
];

const steps = [
  {
    id: 1,
    title: 'Crea el paciente',
    description: 'Nombre, fecha de nacimiento, contacto y alertas básicas. Agrega foto para identificar rápido.',
    tips: ['Define alergias y alertas de inmediato', 'Guarda contacto de emergencia', 'Marca datos obligatorios']
  },
  {
    id: 2,
    title: 'Completa antecedentes',
    description: 'Registra antecedentes personales y familiares, cirugías y reacciones para tener contexto clínico.',
    tips: ['Incluye hábitos relevantes', 'Captura cirugías y fechas', 'Resalta reacciones importantes']
  },
  {
    id: 3,
    title: 'Primera nota médica',
    description: 'Documenta la consulta inicial: motivo, exploración, diagnósticos CIE-10 y plan de acción.',
    tips: ['Usa el buscador CIE-10', 'Deja un plan y seguimiento', 'Guarda borrador si continuarás después']
  },
  {
    id: 4,
    title: 'Genera receta y adjunta estudios',
    description: 'Configura tu firma digital, crea la receta y sube estudios o imágenes que complementen el expediente.',
    tips: ['Aprovecha plantillas de receta', 'Firma digital lista para PDF', 'Clasifica documentos por carpeta']
  }
];

const faqs = [
  {
    id: 1,
    question: '¿Qué incluye el expediente del paciente?',
    answer: 'Incluye identidad, alertas, alergias, antecedentes, notas médicas, diagnósticos CIE-10, recetas con firma digital, documentos, estudios e información personalizada en tablas.'
  },
  {
    id: 2,
    question: '¿Cómo creo mi primer paciente?',
    answer: 'Haz clic en “Crear paciente” y completa identidad, contacto y alertas. Luego agrega antecedentes y registra tu primera nota médica o receta.'
  },
  {
    id: 3,
    question: '¿Puedo subir documentos e imágenes?',
    answer: 'Sí, sube laboratorios, estudios de imagen y anexos. Se organizan por categoría dentro del expediente del paciente.'
  },
  {
    id: 4,
    question: '¿Cómo funcionan las recetas?',
    answer: 'Busca medicamentos, agrega indicaciones, aplica tu firma digital y genera el PDF con tu membrete. Puedes guardar plantillas para reutilizar.'
  },
  {
    id: 5,
    question: '¿Hay datos personalizados?',
    answer: 'Puedes crear columnas y tablas propias en el módulo de datos personalizados, guardar plantillas y exportar a CSV.'
  }
];

const toggleFaq = (id) => {
  activeFaq.value = activeFaq.value === id ? null : id;
};
</script>

<style scoped>
.empty-shell {
  padding: 28px 18px 56px;
  max-width: 1240px;
  margin: 0 auto;
  color: #1f2a3d;
}

.hero {
  position: relative;
  overflow: hidden;
  border-radius: 20px;
  background: linear-gradient(135deg, #fdfefe 0%, #e8f0ff 45%, #eefbf7 100%);
  color: #1f2a3d;
  padding: 28px;
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 24px;
  box-shadow: 0 16px 44px rgba(47, 124, 246, 0.12);
}

.hero::before {
  content: "";
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 18% 22%, rgba(47, 124, 246, 0.14), transparent 40%),
    radial-gradient(circle at 80% 12%, rgba(85, 195, 207, 0.16), transparent 35%),
    radial-gradient(circle at 70% 78%, rgba(189, 230, 237, 0.18), transparent 35%);
}

.hero__content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.hero h1 {
  font-size: 1.9rem;
  margin: 0;
}

.hero p {
  margin: 0;
  color: #324360;
  line-height: 1.6;
}

.hero__actions {
  display: flex;
  gap: 12px;
  margin-top: 8px;
  flex-wrap: wrap;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 18px;
  border-radius: 12px;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid transparent;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.btn i {
  font-size: 14px;
}

.btn--primary {
  background: linear-gradient(135deg, #2f7cf6, #55c3cf);
  color: white;
  box-shadow: 0 10px 20px rgba(47, 124, 246, 0.16);
}

.btn--primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 14px 28px rgba(47, 124, 246, 0.22);
}

.btn--ghost {
  background: rgba(255, 255, 255, 0.55);
  border-color: rgba(47, 124, 246, 0.18);
  color: #1f2a3d;
}

.btn--ghost:hover {
  background: rgba(255, 255, 255, 0.8);
}

.btn.solid {
  background: white;
  color: #0f172a;
}

.pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.2px;
}

.pill--soft {
  background: rgba(47, 124, 246, 0.12);
  color: #1f2a3d;
}

.hero__badges {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 12px;
  margin-top: 10px;
}

.hero-card {
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(47, 124, 246, 0.08);
  border-radius: 16px;
  padding: 12px;
  display: grid;
  gap: 4px;
}

.hero-card__icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.hero-card__title {
  margin: 0;
  font-weight: 700;
  color: white;
}

.hero-card small {
  color: #4b5568;
}

.theme-blue {
  background: linear-gradient(135deg, #60a5fa, #2563eb);
}

.theme-indigo {
  background: linear-gradient(135deg, #818cf8, #4f46e5);
}

.theme-green {
  background: linear-gradient(135deg, #34d399, #059669);
}

.hero__glass {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.glass__stack {
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(47, 124, 246, 0.08);
  border-radius: 16px;
  padding: 14px;
  width: 100%;
  display: grid;
  gap: 10px;
}

.glass__tile {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 12px;
  align-items: center;
  padding: 12px;
  background: rgba(238, 247, 249, 0.8);
  border-radius: 12px;
  border: 1px solid rgba(47, 124, 246, 0.06);
}

.glass__tile i {
  color: #2f7cf6;
}

.kpi-label {
  margin: 0;
  font-size: 13px;
  color: #4b5568;
}

.kpi-value {
  margin: 0;
  font-weight: 700;
  color: #1f2a3d;
}

.section-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  margin: 40px 0 16px;
}

.section-head.center {
  flex-direction: column;
  text-align: center;
  align-items: center;
}

.section-head h2 {
  margin: 4px 0 2px;
  font-size: 1.35rem;
  line-height: 1.35;
}

.eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 12px;
  font-weight: 800;
  color: #4f5f7a;
  margin: 0;
}

.eyebrow.light {
  color: rgba(255, 255, 255, 0.8);
}

.muted {
  color: #4f5f7a;
  margin: 0;
  line-height: 1.6;
}

.muted.light {
  color: rgba(255, 255, 255, 0.75);
}

.chip {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  background: #e8f0ff;
  color: #1f2a3d;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid rgba(47, 124, 246, 0.18);
  font-weight: 700;
  cursor: pointer;
}

.modules__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
}

.templates-hint {
  margin: 18px 0 8px;
}

.templates-card {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 12px;
  align-items: center;
  padding: 14px 16px;
  background: #f7fbff;
  border: 1px solid #e3e8f5;
  border-radius: 14px;
  box-shadow: 0 4px 14px rgba(47, 124, 246, 0.08);
}

.templates-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #2f7cf6, #55c3cf);
  display: grid;
  place-items: center;
  color: white;
}

.templates-icon.alt {
  background: linear-gradient(135deg, #3bc3d4, #7d8be8);
}

.templates-card h3 {
  margin: 2px 0 4px;
  font-size: 1.05rem;
  color: #1f2a3d;
}

.module-card {
  background: white;
  border-radius: 16px;
  padding: 16px;
  border: 1px solid #e5e9f2;
  box-shadow: 0 4px 18px rgba(15, 23, 42, 0.06);
  display: grid;
  gap: 8px;
}

.module-card__header {
  display: flex;
  gap: 12px;
  align-items: center;
}

.module-card__header h3 {
  margin: 2px 0 0;
  font-size: 1.05rem;
  line-height: 1.35;
  color: #1f2a3d;
}

.module-icon {
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  color: #0f172a;
}

.module-label {
  margin: 0;
  font-size: 12px;
  color: #2f7cf6;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.module-desc {
  margin: 0;
  color: #4f5f7a;
  line-height: 1.5;
}

.module-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.module-tags span {
  background: #f4f7fb;
  color: #1f2a3d;
  padding: 6px 10px;
  border-radius: 10px;
  font-size: 13px;
  border: 1px solid #e3e8f5;
}

.steps__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 14px;
  margin-top: 12px;
}

.step-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 12px;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 12px;
  align-items: flex-start;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.07);
}

.step-number {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: linear-gradient(135deg, #6366f1, #22d3ee);
  color: white;
  display: grid;
  place-items: center;
  font-weight: 800;
  font-size: 16px;
}

.step-content h3 {
  margin: 0 0 4px 0;
  font-size: 1.05rem;
  line-height: 1.4;
}

.step-content p {
  margin: 0 0 6px;
  color: #475569;
  font-size: 0.96rem;
}

.step-tips {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 6px;
}

.step-tips li {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  background: #f8fafc;
  border-radius: 10px;
  padding: 8px 10px;
  color: #334155;
}

.step-tips i {
  color: #0ea5e9;
  margin-top: 2px;
}

.faq__list {
  display: grid;
  gap: 10px;
  margin-top: 12px;
}

.faq__item {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 11px 13px;
  cursor: pointer;
  transition: border 0.2s ease, box-shadow 0.2s ease;
}

.faq__item.is-open {
  border-color: #2f7cf6;
  box-shadow: 0 6px 18px rgba(47, 124, 246, 0.12);
}

.faq__question {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}

.faq__question h3 {
  margin: 0;
  font-size: 16px;
}

.faq__question i {
  color: #2f7cf6;
}

.faq__answer {
  margin-top: 8px;
  color: #475569;
  line-height: 1.6;
}

.faq-answer-enter-active,
.faq-answer-leave-active {
  transition: all 0.25s ease;
}

.faq-answer-enter-from,
.faq-answer-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.cta {
  background: linear-gradient(135deg, #2f7cf6, #55c3cf);
  color: #ffffff;
  border-radius: 18px;
  padding: 22px 24px;
  margin-top: 26px;
  box-shadow: 0 14px 32px rgba(47, 124, 246, 0.24);
}

.cta__content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.cta h2 {
  margin: 4px 0 2px;
  font-size: 1.4rem;
}

.cta .btn {
  border: none;
}

.cta .muted.light {
  color: rgba(255, 255, 255, 0.8);
}

.cta__content h2 {
  color: #f8fbff;
  letter-spacing: -0.02em;
}

.cta__content .eyebrow.light {
  color: rgba(255, 255, 255, 0.7);
}

@media (max-width: 1024px) {
  .hero {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 720px) {
  .empty-shell {
    padding: 20px 14px 50px;
  }

  .hero {
    padding: 24px;
  }

  .hero__badges {
    grid-template-columns: 1fr;
  }

  .section-head {
    flex-direction: column;
    align-items: flex-start;
  }

  .steps__grid,
  .modules__grid {
    grid-template-columns: 1fr;
  }

  .cta__content {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
